import { useMemo } from 'react';
import { AgCharts } from 'ag-charts-react';
import { BrgMembershipChartDto } from '../../../types';

interface ChartSectionProps {
  data: BrgMembershipChartDto[];
  colorMap: Map<number, { name: string; color: string }>;
  isLoading: boolean;
  chartType?: 'bar' | 'pie';
}

export function ChartSection({ data, colorMap, isLoading, chartType = 'pie' }: ChartSectionProps) {

  // Map BRG IDs to colors for consistent coloring
  const chartData = useMemo(() => {
    // Sort by member count descending to match the expected visualization
    return data
      .map((item) => ({
        brgId: item.brgId,
        brg: item.brgName,
        members: item.memberCount,
        color: colorMap.get(item.brgId)?.color || '#999',
      }))
      .sort((a, b) => b.members - a.members);
  }, [data, colorMap]);

  // Find max value for scaling bars
  const maxMembers = Math.max(...chartData.map((d) => d.members), 1);

  const pieChartOptions = useMemo(
    () => ({
      data: chartData,
      width: 700,
      height: 420,
      title: {
        text: 'Membership by BRG',
        fontSize: 14,
        fontWeight: 600,
      },
      legend: {
        enabled: false,
      },
      series: [
        {
          type: 'pie' as const,
          angleKey: 'members',
          calloutLabelKey: 'brg',
          fills: chartData.map((item) => item.color),
          calloutLabel: {
            fontSize: 7.5,
            fontWeight: 400,
          },
        },
      ],
    }),
    [chartData]
  );

  if (chartData.length === 0) {
    return (
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          padding: '60px 20px',
          marginBottom: '24px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '3rem', lineHeight: 1, marginBottom: '12px' }}>🔍</div>
        <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', color: '#444' }}>
          No members match the selected filters.
        </h3>
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
          Try adjusting your filters to see results.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      {/* Chart */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '450px',
              color: '#999',
            }}
          >
            <div>Loading chart...</div>
          </div>
        ) : chartType === 'bar' ? (
          <div style={{ width: '100%', padding: '20px 0' }}>
            <h3 style={{ margin: '0 0 24px 0', fontSize: '1.05rem', fontWeight: 600, color: '#333', textAlign: 'center' }}>
              Membership by BRG
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '0 20px' }}>
              {chartData.map((item, idx) => {
                const percentage = (item.members / maxMembers) * 100;
                return (
                  <div key={`bar-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '0 20px' }}>
                    {/* BRG Name */}
                    <div
                      style={{
                        width: '220px',
                        flexShrink: 0,
                        fontSize: '0.93rem',
                        color: '#444',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={item.brg}
                    >
                      {item.brg}
                    </div>

                    {/* Bar container */}
                    <div
                      style={{
                        flex: 1,
                        height: '28px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        border: '1px solid #e8e8e8',
                      }}
                    >
                      {/* Filled bar */}
                      <div
                        style={{
                          height: '100%',
                          width: `${percentage}%`,
                          backgroundColor: item.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          paddingRight: '10px',
                          transition: 'width 0.3s ease-out',
                          minWidth: percentage > 5 ? '30px' : '0px',
                        }}
                      />
                    </div>

                    {/* Count display */}
                    <div
                      style={{
                        width: '50px',
                        flexShrink: 0,
                        textAlign: 'right',
                        fontSize: '0.93rem',
                        fontWeight: 600,
                        color: '#333',
                      }}
                    >
                      {item.members}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <style>{`
              .pie-chart-container canvas {
                filter: drop-shadow(0 0 0 rgba(0,0,0,0));
              }
              .pie-chart-container svg path {
                stroke: none !important;
                stroke-width: 0 !important;
              }
            `}</style>
            <div className="pie-chart-container">
              <AgCharts options={pieChartOptions} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
